import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';

import { ToastService } from '../services/toast.service';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const SKIP_URL_PARTS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/change-password',
  '/hubs/',
  '/health',
  '/swagger'
];

function shouldToast(url: string, method: string, skipHeader: string | null): boolean {
  if (skipHeader === '1' || skipHeader === 'true') return false;
  if (!MUTATING.has(method.toUpperCase())) return false;
  const lower = url.toLowerCase();
  return !SKIP_URL_PARTS.some((p) => lower.includes(p));
}

function pickMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const obj = body as Record<string, unknown>;
  const message = obj['message'] ?? obj['Message'];
  if (typeof message === 'string' && message.trim()) return message.trim();
  return null;
}

function defaultMessage(method: string): string {
  switch (method.toUpperCase()) {
    case 'POST':
      return 'Record saved successfully.';
    case 'PUT':
    case 'PATCH':
      return 'Record updated successfully.';
    case 'DELETE':
      return 'Record deleted successfully.';
    default:
      return 'Action completed successfully.';
  }
}

function pickErrorMessage(err: HttpErrorResponse): string {
  const fromBody = pickMessage(err.error);
  if (fromBody) return fromBody;

  if (typeof err.error === 'string' && err.error.trim()) return err.error.trim();

  if (err.status === 0) return 'Unable to reach the server. Check your connection.';
  if (err.status === 401) return 'Session expired. Please sign in again.';
  if (err.status === 403) return 'You do not have permission for this action.';
  if (err.status === 404) return 'Record not found.';
  if (err.status >= 500) return 'Server error. Please try again.';

  return err.message || 'Request failed.';
}

/**
 * Shows a popup toast for create / update / delete HTTP calls app-wide.
 */
export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const skip = req.headers.get('X-Skip-Toast');
  const notify = shouldToast(req.url, req.method, skip);

  return next(req).pipe(
    tap((event) => {
      if (!notify || !(event instanceof HttpResponse)) return;
      // Blob downloads / empty bodies: still toast for DELETE etc.
      if (event.status < 200 || event.status >= 300) return;

      const body = event.body as Record<string, unknown> | null;
      if (body && typeof body === 'object' && body['success'] === false) {
        toast.error(pickMessage(body) || 'Request failed.');
        return;
      }

      toast.success(pickMessage(body) || defaultMessage(req.method));
    }),
    catchError((err: HttpErrorResponse) => {
      if (notify) {
        toast.error(pickErrorMessage(err));
      }
      return throwError(() => err);
    })
  );
};
